import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Pencil, ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { tenantsApi, usersApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/api/errors";
import type { TenantDto, UserDto } from "@/lib/api/types";
import { ROLES, TENANT_ROLES, roleLabel, type Role } from "@/lib/auth/rbac";
import { useAuthStore } from "@/lib/auth/store";
import { useToast } from "@/hooks/use-toast";

/** Sentinel values for the restaurant filter select (Radix Select can't use ""). */
const FILTER_ALL = "ALL";
const FILTER_PLATFORM = "PLATFORM";

export default function UsersPage() {
  const me = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [tenantFilter, setTenantFilter] = useState<string>(FILTER_ALL);

  const users = useQuery({ queryKey: ["users", "all"], queryFn: () => usersApi.list() });
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: tenantsApi.list });

  const tenantName = useMemo(() => {
    const map = new Map<number, string>();
    (tenants.data ?? []).forEach((t) => map.set(t.id, t.name));
    return (id: number | null) => (id == null ? null : (map.get(id) ?? `#${id}`));
  }, [tenants.data]);

  /** The user's business type, so WAITER reads "Seller" for a shop. */
  const tenantType = useMemo(() => {
    const map = new Map<number, string>();
    (tenants.data ?? []).forEach((t) => map.set(t.id, t.businessType));
    return (id: number | null) => (id == null ? null : (map.get(id) ?? null));
  }, [tenants.data]);

  const filtered = useMemo(() => {
    const all = users.data ?? [];
    if (tenantFilter === FILTER_ALL) return all;
    if (tenantFilter === FILTER_PLATFORM) return all.filter((u) => u.tenantId == null);
    const id = Number(tenantFilter);
    return all.filter((u) => u.tenantId === id);
  }, [users.data, tenantFilter]);

  const toggleActive = useMutation({
    mutationFn: (user: UserDto) => usersApi.update(user.id, { active: !user.active }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: `${updated.fullName} ${updated.active ? "activated" : "deactivated"}`,
        variant: "success",
      });
    },
    onError: (err) =>
      toast({ title: "Couldn't update user", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <div>
      <PageHeader
        title="Users"
        description="Every account on the platform — admins and business staff"
        actions={
          <>
            <Select value={tenantFilter} onValueChange={setTenantFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTER_ALL}>All businesses</SelectItem>
                <SelectItem value={FILTER_PLATFORM}>Platform (admins)</SelectItem>
                {(tenants.data ?? []).map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New user
            </Button>
          </>
        }
      />

      <Card>
        {users.isLoading && <Skeleton className="m-5 h-48" />}
        {users.data && filtered.length === 0 && (
          <EmptyState icon={Users} title="No users" description="Nothing matches this filter yet." />
        )}
        {filtered.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Username</TableHead>
                <TableHead className="hidden sm:table-cell">Full name</TableHead>
                <TableHead className="hidden md:table-cell">Business</TableHead>
                <TableHead className="hidden sm:table-cell">Role</TableHead>
                <TableHead className="hidden md:table-cell">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-mono font-medium">
                    {user.username}
                    {/* Phones: the hidden columns, folded under the username. */}
                    <div className="mt-1 font-sans text-xs font-normal text-muted-foreground sm:hidden">
                      {user.fullName} · {roleLabel(user.role, tenantType(user.tenantId))}
                      {user.tenantId != null && ` · ${tenantName(user.tenantId) ?? "—"}`}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{user.fullName}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {user.tenantId == null ? (
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5" /> Platform
                      </span>
                    ) : (
                      (tenantName(user.tenantId) ?? "—")
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={user.role === "SUPER_ADMIN" ? "default" : "secondary"}>
                      {roleLabel(user.role, tenantType(user.tenantId))}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant={user.active ? "success" : "destructive"}>
                      {user.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="space-y-1 text-right sm:space-x-2 sm:space-y-0 sm:whitespace-nowrap">
                    <Button variant="outline" size="sm" onClick={() => setEditing(user)}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    {user.id !== me?.userId && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={toggleActive.isPending}
                        onClick={() => toggleActive.mutate(user)}
                      >
                        {user.active ? "Deactivate" : "Activate"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={creating || !!editing} onOpenChange={(o) => !o && (setCreating(false), setEditing(null))}>
        {(creating || editing) && (
          <UserDialog
            user={editing}
            tenants={tenants.data ?? []}
            onDone={() => {
              setCreating(false);
              setEditing(null);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

/** Sentinel for "no restaurant" in the dialog's restaurant select. */
const NO_TENANT = "NONE";

function UserDialog({
  user,
  tenants,
  onDone,
}: {
  user: UserDto | null;
  tenants: TenantDto[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [username, setUsername] = useState(user?.username ?? "");
  const [fullName, setFullName] = useState(user?.fullName ?? "");
  const [role, setRole] = useState<Role>(user?.role ?? "WAITER");
  const [tenantValue, setTenantValue] = useState<string>(
    user?.tenantId != null ? String(user.tenantId) : user?.role === "SUPER_ADMIN" ? NO_TENANT : (tenants[0] ? String(tenants[0].id) : NO_TENANT),
  );
  const [password, setPassword] = useState("");

  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  // A platform account stays a platform account; a staff account stays with its restaurant.
  const roleOptions: Role[] = user
    ? user.tenantId == null
      ? [ROLES.SUPER_ADMIN]
      : TENANT_ROLES
    : [ROLES.SUPER_ADMIN, ...TENANT_ROLES];

  function handleRoleChange(next: Role) {
    setRole(next);
    if (next === ROLES.SUPER_ADMIN) {
      setTenantValue(NO_TENANT);
    } else if (tenantValue === NO_TENANT && tenants[0]) {
      setTenantValue(String(tenants[0].id));
    }
  }

  const save = useMutation({
    mutationFn: () => {
      if (user) {
        return usersApi.update(user.id, {
          fullName: fullName.trim(),
          role,
          ...(password ? { password } : {}),
        });
      }
      return usersApi.create({
        tenantId: isSuperAdmin || tenantValue === NO_TENANT ? null : Number(tenantValue),
        username: username.trim(),
        password,
        fullName: fullName.trim(),
        role,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({ title: user ? "User updated" : "User created", variant: "success" });
      onDone();
    },
    onError: (err) => toast({ title: "Couldn't save user", description: apiErrorMessage(err), variant: "destructive" }),
  });

  const missingTenant = !user && !isSuperAdmin && tenantValue === NO_TENANT;

  return (
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle>{user ? `Edit ${user.username}` : "New user"}</DialogTitle>
      </DialogHeader>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label>Username</Label>
          <Input
            autoFocus={!user}
            required
            disabled={!!user}
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            placeholder="e.g. asha"
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Full name</Label>
          <Input required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Emma" />
        </div>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select value={role} onValueChange={(v) => handleRoleChange(v as Role)}>
            <SelectTrigger disabled={roleOptions.length === 1}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((r) => (
                <SelectItem key={r} value={r}>
                  {roleLabel(r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Business</Label>
          <Select value={tenantValue} onValueChange={setTenantValue} disabled={!!user || isSuperAdmin}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a business" />
            </SelectTrigger>
            <SelectContent>
              {isSuperAdmin && <SelectItem value={NO_TENANT}>— Platform (no business)</SelectItem>}
              {tenants.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isSuperAdmin && (
            <p className="text-xs text-muted-foreground">Super admins belong to the platform, not a business.</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>{user ? "New password (leave blank to keep)" : "Password"}</Label>
          <Input
            type="password"
            required={!user}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <DialogFooter>
          <Button
            type="submit"
            disabled={
              save.isPending || !fullName.trim() || missingTenant || (!user && (!username.trim() || !password))
            }
          >
            {save.isPending ? "Saving…" : user ? "Save changes" : "Create user"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
